import React, { useState, useEffect } from 'react';

/**
 * PollingConfig Component
 *
 * Provides user controls for configuring RPC polling frequency and visual feedback.
 * Shows current polling frequency, allows user to adjust it, and displays poll status.
 */
const PollingConfig = () => {
  const [frequency, setFrequency] = useState(10); // Default 10 calls/minute
  const [lastPollStatus, setLastPollStatus] = useState(null); // 'success', 'error', or null
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPaused, setIsPaused] = useState(false); // Temporary pause state (NOT persisted)
  const [pauseReason, setPauseReason] = useState(''); // Why it's paused: 'manual' or 'game-finished'

  // Load frequency from localStorage on component mount
  useEffect(() => {
    const savedFrequency = localStorage.getItem('rpc_polling_frequency');
    if (savedFrequency) {
      const parsedFrequency = parseInt(savedFrequency, 10);
      if (parsedFrequency >= 0 && parsedFrequency <= 60) {
        setFrequency(parsedFrequency);
      }
    }
  }, []);

  // Listen for poll status events from BlockchainDataManager
  useEffect(() => {
    const handlePollStatus = (event) => {
      console.log(`🎨 PollingConfig received status: ${event.detail.status}`);
      setLastPollStatus(event.detail.status);

      // Clear status after 2 seconds
      setTimeout(() => {
        setLastPollStatus(null);
      }, 2000);
    };

    // Listen for custom events from BlockchainDataManager
    window.addEventListener('blockchainPollStatus', handlePollStatus);

    return () => {
      window.removeEventListener('blockchainPollStatus', handlePollStatus);
    };
  }, []);

  // Listen for auto-pause from game pages
  useEffect(() => {
    const handleAutoPause = (event) => {
      setIsPaused(event.detail.isPaused);
      setPauseReason(event.detail.reason);
    };

    window.addEventListener('setPollingPause', handleAutoPause);
    return () => window.removeEventListener('setPollingPause', handleAutoPause);
  }, []);

  // Handle frequency change
  const handleFrequencyChange = (newFrequency) => {
    if (newFrequency >= 0 && newFrequency <= 60) {
      setFrequency(newFrequency);
      localStorage.setItem('rpc_polling_frequency', newFrequency.toString());

      // Emit event to notify BlockchainDataManager of the change
      window.dispatchEvent(new CustomEvent('pollingFrequencyChanged', {
        detail: { frequency: newFrequency }
      }));
    }
  };

  // Handle manual refresh
  const handleManualRefresh = () => {
    // Emit event to trigger manual poll
    window.dispatchEvent(new CustomEvent('manualPollRequested'));
  };

  // Handle pause/resume toggle
  const handlePauseToggle = () => {
    const newPaused = !isPaused;
    setIsPaused(newPaused);
    setPauseReason(newPaused ? 'manual' : '');

    // Notify BlockchainDataManager
    window.dispatchEvent(new CustomEvent('pollingPauseChanged', {
      detail: { isPaused: newPaused, reason: newPaused ? 'manual' : '' }
    }));
  };

  // Get status indicator classes
  const getStatusClasses = () => {
    if (isPaused) {
      return 'bg-yellow-500'; // Yellow for paused state
    } else if (lastPollStatus === 'success') {
      return 'bg-green-500 animate-pulse';
    } else if (lastPollStatus === 'error') {
      return 'bg-red-500 animate-pulse';
    }
    return 'bg-gray-300';
  };

  // Get display text for frequency
  const getDisplayText = () => {
    if (isPaused) return 'Paused';
    if (frequency === 0) return 'Manual';
    return `${frequency}/min`;
  };

  // Get tooltip text
  const getStatusTooltip = () => {
    if (isPaused && pauseReason === 'game-finished') return 'Paused - game finished';
    if (isPaused && pauseReason === 'manual') return 'Paused - manually disabled';
    if (frequency === 0) return 'Manual mode - no automatic polling';
    return `Polling every ${Math.round(60/frequency)}s`;
  };

  return (
    <div className="relative flex items-center space-x-2">
      {/* Manual refresh button */}
      <button
        onClick={handleManualRefresh}
        className="p-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        title="Manual refresh"
      >
        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      </button>

      {/* Main polling config button with status indicator */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center space-x-2 p-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        title={getStatusTooltip()}
      >
        {/* Status indicator dot */}
        <div className={`w-3 h-3 rounded-full ${getStatusClasses()}`}></div>

        {/* Frequency display */}
        <span className="text-sm font-medium text-gray-700">
          {getDisplayText()}
        </span>

        {/* Expand/collapse arrow */}
        <svg
          className={`w-4 h-4 text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded configuration panel */}
      {isExpanded && (
        <div className="absolute top-full mt-1 right-0 w-64 bg-white border border-gray-300 rounded-md shadow-lg z-50 p-4">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                RPC Polling Frequency
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="range"
                  min="0"
                  max="60"
                  value={frequency}
                  onChange={(e) => handleFrequencyChange(parseInt(e.target.value, 10))}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex items-center space-x-1">
                  <input
                    type="number"
                    min="0"
                    max="60"
                    value={frequency}
                    onChange={(e) => handleFrequencyChange(parseInt(e.target.value, 10) || 0)}
                    className="w-12 px-1 py-1 text-sm border border-gray-300 rounded text-center"
                  />
                  <span className="text-sm text-gray-600">/min</span>
                </div>
              </div>
            </div>

            {/* Pause/Resume Toggle */}
            <div className="pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Polling Status</span>
                <button
                  onClick={handlePauseToggle}
                  className={`px-3 py-1 text-sm rounded font-medium transition-colors ${
                    isPaused
                      ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                      : 'bg-green-100 text-green-800 hover:bg-green-200'
                  }`}
                >
                  {isPaused ? 'Resume' : 'Pause'}
                </button>
              </div>
              {pauseReason && (
                <p className="text-xs text-gray-500 mt-1">
                  {pauseReason === 'game-finished' ? 'Auto-paused: game finished' : 'Manually paused'}
                </p>
              )}
            </div>

            <div className="text-xs text-gray-500 space-y-1">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>Success - Polling working normally</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span>Error - Rate limited or network issues</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span>Paused - Polling temporarily disabled</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                <span>Idle - No recent polls</span>
              </div>
            </div>

            <div className="pt-2 border-t border-gray-200">
              <p className="text-xs text-gray-600">
                Set to 0 for manual-only polling. Lower frequencies reduce rate limiting but slow updates.
                Higher frequencies provide faster updates but may trigger rate limits.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PollingConfig;